import {
  Switch,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useContext } from 'react';
import { usePermissionStore } from '../../../models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import { useTranslation } from 'react-i18next';
import { PolicyTypeEnum } from '../utils/constants';
import {
  OverAllPolicyType,
  PolicyItemInTable,
  PolicyListModalName,
  PolicyListModalState,
} from '../utils/types';
import { OverAllPolicyTypeContext } from '../OverAllPolicyList/context/OverAllPolicyTypeContext';
import { useGlobalSettingStore } from '@Models/useGlobalSettingModel';
import { checkIfPolicyInTableHasEnabledFilter } from '@Policy/utils/fns';
import { usePolicyMutation } from '../OverAllPolicyList/context/PolicyMutationContext';

interface Props {
  policy: PolicyItemInTable;
  setModal: ({ name, policy }: PolicyListModalState) => void;
  disabled: boolean;
  collectionType: any;
}

export const PolicyStatusSwitch = ({
  policy,
  setModal,
  disabled: disabledProp,
}: Props) => {
  const { t } = useTranslation();
  const overAllPolicyType = useContext(OverAllPolicyTypeContext);
  const { widthEditablePermission } = usePermissionStore();
  const editPermission = widthEditablePermission(
    overAllPolicyType === OverAllPolicyType.ATP
      ? PermissionNameEnum.atpPolicy
      : PermissionNameEnum.dlpPolicy
  );
  const {
    policyUuid,
    policyRealtimeEnabled,
    policyMode,
    policyType,
    policyTargetCapacity,
  } = policy;
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();

  // Get mutation functions and state from context
  const { statusMutation } = usePolicyMutation();

  // if false, show popover
  let policyRealtimeSupport = true;
  let popoverStr = '';
  if (policy.policyType === PolicyTypeEnum.GoogleDrive) {
    policyRealtimeSupport =
      !!selectedOrgGlobalSetting.googleDriveRealtimeScanSupport;
    popoverStr = t('policy.tooltip.googleNotRealtimeScan');
  }
  if (policyTargetCapacity === 'none' && !policyRealtimeEnabled) {
    policyRealtimeSupport = false;
    popoverStr = t('policy.tooltip.noTargetCannotEnable');
  }

  return (
    <Popover
      arrow={true}
      trigger="hover"
      placement="top"
      enterDelay={500}
      disabled={policyRealtimeSupport}
    >
      <PopoverTrigger>
        <Switch
          // TODO: 加上老ui的这个判断disable_policy
          disabled={
            !editPermission ||
            policyMode === 3 ||
            !policyRealtimeSupport ||
            disabledProp
          }
          size="sm"
          width="auto"
          checked={!!policyRealtimeEnabled}
          onChange={async () => {
            // Make sure at least one filter is enabled for ATP and DLP policies
            const hasEnabledFilter =
              checkIfPolicyInTableHasEnabledFilter(policy);
            if (!hasEnabledFilter) {
              window.showNotification({
                titleMsg: t('errors.policy.atLeastOneFilterEnabled'),
                severity: 'error',
              });
              return;
            }

            // Use optimistic mutation to toggle status
            statusMutation.mutate(
              {
                policyId: policyUuid,
                currentStatus: policyRealtimeEnabled,
              },
              {
                onSuccess: () => {
                  window.showNotification({
                    titleMsg: t('policy.toast.changePolicyStatus.success'),
                    severity: 'success',
                  });
                },
              }
            );

            // Handle Gmail modal after status change
            if (
              policyType === PolicyTypeEnum.GmailInline &&
              policyRealtimeEnabled === 0
            ) {
              setModal({
                name: PolicyListModalName.GmailNote,
                policy: policy as PolicyItemInTable,
              });
            }
          }}
        />
      </PopoverTrigger>
      <PopoverContent
        maxWidth="600px"
        PopperProps={{
          usePortal: true,
        }}
      >
        <Text color={colorStyle.color.primary}>{popoverStr}</Text>
      </PopoverContent>
    </Popover>
  );
};
